"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { sendOtp, verifyOtp } from "@/app/auth/actions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Mail, Shield } from "lucide-react";

type AuthStep = "email" | "code";

export function AuthForm() {
  const [step, setStep] = useState<AuthStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/dashboard';

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await sendOtp(email);

      if (error) {
        const msg = error.toLowerCase();
        if (
          msg.includes("failed to fetch") ||
          msg.includes("network") ||
          msg.includes("err_network")
        ) {
          setError("Falha de conexão com o servidor. Verifique sua internet e tente novamente.");
        } else {
          setError(error || "Ocorreu um erro. Tente novamente.");
        }
        return;
      }

      setStep("code");
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await verifyOtp(email, code);

      if (error) {
        setError("Código inválido ou expirado. Tente novamente.");
        return;
      }

      router.push(redirectTo);
      router.refresh();
    } catch {
      setError("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "code") {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <Shield className="h-12 w-12 text-blue-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Digite o Código
          </CardTitle>
          <CardDescription className="text-center">
            Enviamos um código de 8 dígitos para <strong>{email}</strong>
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleVerifyCode}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code" className="text-center block">Código de Verificação</Label>
              <Input
                id="code"
                type="text"
                placeholder="12345678"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                required
                disabled={loading}
                autoFocus
                className="text-center text-2xl tracking-widest"
                maxLength={8}
              />
            </div>
            {error && (
              <div className="text-sm p-3 rounded-md bg-destructive/10 text-destructive">
                {error}
              </div>
            )}
            <div className="text-xs text-muted-foreground text-center">
              Não recebeu? Verifique a pasta de spam ou aguarde alguns minutos.
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <Button
              type="submit"
              className="w-full"
              disabled={loading || code.length !== 8}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verificar Código
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => {
                setStep("email");
                setCode("");
                setError(null);
              }}
              disabled={loading}
            >
              Usar outro email
            </Button>
          </CardFooter>
        </form>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <div className="flex justify-center mb-4">
          <Mail className="h-12 w-12 text-blue-500" />
        </div>
        <CardTitle className="text-2xl font-bold text-center">
          Entrar no Insight
        </CardTitle>
        <CardDescription className="text-center">
          Digite seu email e enviaremos um código de 8 dígitos
        </CardDescription>
      </CardHeader>
      <form onSubmit={handleSendCode}>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              autoFocus
            />
          </div>
          {error && (
            <div className="text-sm p-3 rounded-md bg-destructive/10 text-destructive">
              {error}
            </div>
          )}
        </CardContent>
        <CardFooter>
          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enviar Código
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
